// src/ryan/admin/components/tabs/RyanAdminAnalytics.tsx
//
// Analytics de funil por produto. Dois modos dentro da mesma tela, mesmos
// filtros (produto/período/origem) compartilhados:
//   - Funil (Fase 2): contagem sequencial visitantes -> pagamento aprovado.
//   - Comportamento (Fase 3): "resumo executivo" das mesmas etapas como
//     percentuais/médias, mais tempo médio na página e marcos de rolagem.
//     Nenhum evento/schema novo — só uma leitura agregada diferente dos
//     mesmos dados coletados desde a Fase 1.
//   - Timeline (Fase 4): lista de sessões pra escolher qual investigar +
//     timeline cronológica de eventos de uma sessão específica — delegado
//     inteiro pra RyanAdminAnalyticsTimeline.tsx (tem estado próprio de
//     paginação/busca/sessão selecionada, não faz sentido no componente pai).
//   - Divulgação (Fase 5): registro manual de envios (WhatsApp/outro) +
//     alcance calculado por janela de tempo a partir do envio, cruzando só
//     com source:'direct' — delegado pra RyanAdminAnalyticsOutreach.tsx. O
//     pill de origem da tela-mãe NÃO se aplica aqui (o cálculo é sempre
//     'direct' por definição, não por escolha do usuário), por isso esse
//     modo esconde o pill em vez de deixar um filtro que não faz nada.
//   - Leads Pendentes: delegado pra RyanAdminLeads.tsx — deliberadamente
//     SEPARADO do resto em termos de DADOS (RyanAnalyticsEvent anônimo vs
//     RyanAccessRequest com dado pessoal de propósito, ver comentário no topo
//     daquele arquivo). Produto/período dos outros 4 modos não se aplicam
//     aqui (produto é OPCIONAL — cruza produtos por padrão — e período tem só
//     3 presets, sem "customizado"), então este componente mantém estado
//     PRÓPRIO pra esses dois (leadsProductId/leadsPeriod) e passa como props
//     controladas pro RyanAdminLeads, que não busca nem renderiza esses
//     filtros mais sozinho — ficam na MESMA linha do select de modo (antes
//     eram uma segunda linha só do RyanAdminLeads, quebrando o layout em
//     duas linhas sempre que esse modo estava ativo).
//
// Filtro por embaixador específico (repCode) — só existe em Funil e
// Comportamento (agregado), nunca em Timeline: selecionar um embaixador
// esconde o pill de origem (redundante, repCode já implica 'referral') e
// filtra as contagens; trocar pra Timeline com um repCode selecionado volta
// a mostrar TODAS as sessões, de propósito — é a garantia de LGPD que só
// permite cruzar embaixador específico com AGREGADO, nunca com a navegação
// individual de uma pessoa (ver RyanAnalyticsEvent.js e os handlers de
// /sessions em ryanRoutes.js).

import React, { useEffect, useState } from 'react';
import RyanAdminAnalyticsTimeline from './RyanAdminAnalyticsTimeline';
import RyanAdminAnalyticsOutreach from './RyanAdminAnalyticsOutreach';
import RyanAdminLeads from './RyanAdminLeads';
import RyanAdminDropdown from '../RyanAdminDropdown';
import { isOptedOut, setOptedOut } from '../../../utils/ryanAnalytics';
import './RyanAdminAnalytics.css';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string) => fetch(`/api/ryan${path}`, { headers: { Authorization: `Bearer ${tok()}` } });

interface Product {
    _id: string;
    title: string;
    publicPage?: { enabled?: boolean };
}

interface FunnelStep {
    key: string;
    label: string;
    count: number | null;
}

interface FunnelResponse {
    steps: FunnelStep[];
    period: string;
    source: string;
    repCode: string | null;
}

interface Representative {
    _id: string;
    name: string;
    referralCode: string;
}

interface BehaviorResponse {
    period: string;
    source: string;
    repCode: string | null;
    visitors: number;
    avgTimeOnPageSeconds: number | null;
    avgTimeOnPageSampleSize: number;
    scrollMilestones: { '25': number; '50': number; '75': number; '100': number };
    video: { watchedPercent: number; avgWatchedPercent: number | null } | null;
    previewPercent: number;
    buyClickPercent: number;
    checkoutStartedPercent: number;
    conversionPercent: number;
}

type ViewMode = 'funnel' | 'behavior' | 'timeline' | 'outreach' | 'leads';

const PERIODS: { value: string; label: string }[] = [
    { value: '7d', label: '7 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
    { value: 'custom', label: 'Período customizado' },
];

// Mesmos 3 presets que RyanAdminLeads.tsx sempre usou sozinho (sem
// "customizado" — não existe essa granularidade no endpoint de leads).
const LEADS_PERIODS: { value: string; label: string }[] = [
    { value: '7d', label: '7 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
];

// Formato YYYY-MM-DD (o que <input type="date"> já produz/consome) — hoje
// como padrão ao trocar pra "Período customizado", pra nunca disparar um
// fetch com from/to vazios.
function todayISODate(): string {
    return new Date().toISOString().slice(0, 10);
}

// 'direct'/'referral', nunca o código específico de embaixador — decisão de
// LGPD já fechada na Fase 0 (ver RyanAnalyticsEvent.js). Cruzamento por
// embaixador específico é feito à parte, sobre RyanCommissionEntry (vendas
// reais), nunca sobre este funil/comportamento de navegação anônima.
const SOURCES: { value: string; label: string }[] = [
    { value: 'all', label: 'Total' },
    { value: 'direct', label: 'Direto' },
    { value: 'referral', label: 'Indicação' },
];

const VIEWS: { value: ViewMode; label: string }[] = [
    { value: 'funnel', label: 'Funil' },
    { value: 'behavior', label: 'Comportamento' },
    { value: 'timeline', label: 'Timeline' },
    { value: 'outreach', label: 'Divulgação' },
    { value: 'leads', label: 'Leads Pendentes' },
];

function conversionPercent(prev: number | null, curr: number | null): number | null {
    if (prev === null || curr === null || prev <= 0) return null;
    return Math.round((curr / prev) * 100);
}

// Exportado — RyanAdminAnalyticsTimeline.tsx reaproveita a mesma formatação
// pra duração de sessão (mesma unidade, "1m 24s"/"45s").
export function formatDuration(seconds: number | null): string {
    if (seconds === null) return '—';
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
}

interface StatTile { value: string; label: string; sub?: string }

function buildBehaviorTiles(b: BehaviorResponse): StatTile[] {
    const tiles: StatTile[] = [
        {
            value: formatDuration(b.avgTimeOnPageSeconds),
            label: 'Tempo médio na página',
            sub: `${b.avgTimeOnPageSampleSize.toLocaleString('pt-BR')} sessões`,
        },
        { value: `${b.scrollMilestones['25']}%`, label: 'Rolaram até 25%' },
        { value: `${b.scrollMilestones['50']}%`, label: 'Rolaram até 50%' },
        { value: `${b.scrollMilestones['75']}%`, label: 'Rolaram até 75%' },
        { value: `${b.scrollMilestones['100']}%`, label: 'Rolaram até o fim' },
    ];
    if (b.video) {
        tiles.push(
            { value: `${b.video.watchedPercent}%`, label: 'Assistiram à demonstração' },
            {
                value: b.video.avgWatchedPercent !== null ? `${b.video.avgWatchedPercent}%` : '—',
                label: 'Média assistida do vídeo',
                sub: 'entre quem clicou em play',
            },
        );
    }
    tiles.push(
        { value: `${b.previewPercent}%`, label: 'Abriram alguma prévia' },
        { value: `${b.buyClickPercent}%`, label: 'Clicaram em comprar' },
        { value: `${b.checkoutStartedPercent}%`, label: 'Iniciaram checkout', sub: 'sobre quem clicou em comprar' },
        { value: `${b.conversionPercent}%`, label: 'Conversão final', sub: 'pagamento aprovado / visitantes' },
    );
    return tiles;
}

const RyanAdminAnalytics: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [productId, setProductId] = useState('');
    const [period, setPeriod] = useState('30d');
    const [customFrom, setCustomFrom] = useState(todayISODate());
    const [customTo, setCustomTo] = useState(todayISODate());
    const [source, setSource] = useState('all');
    // Embaixador específico — só usado em Funil/Comportamento (ver comentário
    // no topo do arquivo). '' = nenhum selecionado ("Todos").
    const [repCode, setRepCode] = useState('');
    const [representatives, setRepresentatives] = useState<Representative[]>([]);
    const [view, setView] = useState<ViewMode>('funnel');
    // Leads Pendentes tem produto OPCIONAL (cruza produtos por padrão) e uma
    // lista de produtos própria, sem o filtro publicPage.enabled aplicado
    // pros outros 4 modos (um lead pode existir pra um produto que teve a
    // página pública desativada depois) — por isso estado e fetch à parte,
    // nunca reaproveitando `products`/`productId` de cima.
    const [leadsProducts, setLeadsProducts] = useState<Product[]>([]);
    const [leadsProductId, setLeadsProductId] = useState('');
    const [leadsPeriod, setLeadsPeriod] = useState('90d');
    const [funnel, setFunnel] = useState<FunnelResponse | null>(null);
    const [behavior, setBehavior] = useState<BehaviorResponse | null>(null);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingView, setLoadingView] = useState(false);
    // Opt-out de analytics (Item 3) — configuração por NAVEGADOR, não por
    // produto/período, por isso fora do fluxo de fetch acima. Ligada por
    // padrão automaticamente na primeira vez que este navegador vê um admin
    // logado (ver RyanAdminPage.tsx) — aqui só espelha o estado atual e
    // permite mudar manualmente.
    const [optedOut, setOptedOutState] = useState(false);

    useEffect(() => { setOptedOutState(isOptedOut()); }, []);

    const toggleOptedOut = () => {
        const next = !optedOut;
        setOptedOut(next);
        setOptedOutState(next);
    };

    useEffect(() => {
        api('/admin/products').then(r => r.ok ? r.json() : []).then((list: Product[]) => {
            // Analytics de visitante só faz sentido pra produtos com página
            // pública ativa — os únicos que geram eventos client-side.
            const withPublicPage = (Array.isArray(list) ? list : []).filter(p => p.publicPage?.enabled);
            setProducts(withPublicPage);
            if (withPublicPage.length > 0) setProductId(withPublicPage[0]._id);
            setLoadingProducts(false);
        });
    }, []);

    useEffect(() => {
        api('/admin/products').then(r => r.ok ? r.json() : []).then((list: Product[]) => {
            setLeadsProducts(Array.isArray(list) ? list : []);
        });
    }, []);

    // Lista de embaixadores do produto selecionado, pro seletor de repCode —
    // refeita a cada troca de produto (um embaixador vinculado ao produto A
    // pode não estar vinculado ao B). repCode reseta junto: um valor válido
    // pro produto anterior pode não existir mais na lista nova.
    useEffect(() => {
        setRepCode('');
        if (!productId) { setRepresentatives([]); return; }
        api(`/admin/analytics/representatives?productId=${productId}`)
            .then(r => r.ok ? r.json() : [])
            .then((list: Representative[]) => setRepresentatives(Array.isArray(list) ? list : []));
    }, [productId]);

    // 'Período customizado' só é válido com from/to preenchidos e from <= to
    // (nunca um intervalo invertido) — enquanto inválido, periodParams fica
    // null e os 3 pontos de fetch (aqui, Timeline, Divulgação) sabem esperar
    // em vez de disparar uma chamada com data quebrada.
    const customRangeInvalid = period === 'custom' && (!customFrom || !customTo || customFrom > customTo);
    const periodParams: Record<string, string> | null = customRangeInvalid
        ? null
        : period === 'custom'
            ? { period: 'custom', from: customFrom, to: customTo }
            : { period };

    // Só busca o modo ATIVO — /behavior é uma query mais pesada (tempo médio
    // na página cruza todos os eventos por sessão, não só contagens), não faz
    // sentido pagar esse custo enquanto a aba Funil é a que está visível.
    // Timeline e Divulgação não entram aqui: cada uma tem seu próprio fetch
    // (paginação/busca/sessão selecionada na Timeline; formulário+janela na
    // Divulgação), gerenciados dentro do respectivo componente delegado.
    useEffect(() => {
        if (!productId || view === 'timeline' || view === 'outreach' || view === 'leads' || !periodParams) return;
        setLoadingView(true);
        const qs = new URLSearchParams({
            ...periodParams, source,
            ...(repCode ? { repCode } : {}),
        }).toString();
        api(`/admin/analytics/${productId}/${view}?${qs}`)
            .then(r => r.ok ? r.json() : null)
            .then(data => { view === 'funnel' ? setFunnel(data) : setBehavior(data); })
            .finally(() => setLoadingView(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, period, customFrom, customTo, source, repCode, view]);

    if (loadingProducts) {
        return <div className="ras-pane"><div className="ras-spinner-wrap"><div className="ras-spinner" /></div></div>;
    }

    // Diferente dos outros 4 modos, Leads Pendentes NÃO depende de produto
    // com página pública (cruza produtos por padrão, ver RyanAdminLeads.tsx)
    // — por isso products.length===0 não pode mais ser um early-return que
    // bloqueia a tela inteira: precisa deixar o select de modo visível pra
    // dar pra alcançar Leads Pendentes mesmo sem nenhum produto elegível
    // pros outros 4.
    const noEligibleProducts = products.length === 0;

    const steps = funnel?.steps || [];
    const funnelTotal = steps[0]?.count || 0;

    return (
        <div className="ras-pane">
            {view !== 'leads' && (
                <label className="rana-optout-row">
                    <input type="checkbox" checked={optedOut} onChange={toggleOptedOut} />
                    Modo teste: não rastrear minhas visitas às páginas públicas neste navegador
                </label>
            )}
            <div className="rana-filters">
                {view === 'leads' ? (
                    <RyanAdminDropdown
                        className="ras-dd--product"
                        options={[{ value: '', label: 'Todos os produtos' }, ...leadsProducts.map(p => ({ value: p._id, label: p.title }))]}
                        value={leadsProductId} onChange={setLeadsProductId}
                    />
                ) : !noEligibleProducts && (
                    <RyanAdminDropdown
                        className="ras-dd--product"
                        options={products.map(p => ({ value: p._id, label: p.title }))}
                        value={productId} onChange={setProductId}
                    />
                )}
                <RyanAdminDropdown
                    className="ras-dd--mode"
                    options={VIEWS}
                    value={view} onChange={v => setView(v as ViewMode)}
                />
                {view === 'leads' ? (
                    <RyanAdminDropdown
                        className="ras-dd--period"
                        options={LEADS_PERIODS}
                        value={leadsPeriod} onChange={setLeadsPeriod}
                    />
                ) : (
                    <>
                        <RyanAdminDropdown
                            className="ras-dd--period"
                            options={PERIODS}
                            value={period} onChange={setPeriod}
                        />
                        {period === 'custom' && (
                            <>
                                <input
                                    type="date" className="ras-input rana-filter-date"
                                    value={customFrom} max={customTo || undefined}
                                    onChange={e => setCustomFrom(e.target.value)}
                                />
                                <span className="rana-filter-date-sep">até</span>
                                <input
                                    type="date" className="ras-input rana-filter-date"
                                    value={customTo} min={customFrom || undefined}
                                    onChange={e => setCustomTo(e.target.value)}
                                />
                            </>
                        )}
                        {/* repCode implica referral — esconde o pill de origem quando um
                            embaixador específico está selecionado (redundante), mesmo
                            precedente já usado em Divulgação pro pill de origem. */}
                        {view !== 'outreach' && !repCode && (
                            <RyanAdminDropdown
                                className="ras-dd--narrow"
                                options={SOURCES}
                                value={source} onChange={setSource}
                            />
                        )}
                        {/* Só em Funil/Comportamento (agregado) — NUNCA em Timeline, ver
                            comentário no topo do arquivo sobre a garantia de LGPD. */}
                        {(view === 'funnel' || view === 'behavior') && representatives.length > 0 && (
                            <RyanAdminDropdown
                                className="ras-dd--narrow"
                                options={[{ value: '', label: 'Todos os embaixadores' }, ...representatives.map(r => ({ value: r.referralCode, label: r.name }))]}
                                value={repCode} onChange={setRepCode}
                            />
                        )}
                    </>
                )}
            </div>

            {view === 'leads' ? (
                <RyanAdminLeads productId={leadsProductId} period={leadsPeriod} />
            ) : noEligibleProducts ? (
                <div className="ras-section rana-card">
                    <div className="rana-empty">Nenhum produto com página pública ativa ainda.</div>
                </div>
            ) : (
            <div className="ras-section rana-card">
            {view === 'timeline' ? (
                <RyanAdminAnalyticsTimeline productId={productId} periodParams={periodParams} source={source} />
            ) : view === 'outreach' ? (
                <RyanAdminAnalyticsOutreach productId={productId} periodParams={periodParams} />
            ) : loadingView ? (
                <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
            ) : view === 'funnel' ? (
                funnelTotal === 0 ? (
                    <div className="rana-empty">Ainda sem visitas registradas neste período/segmento.</div>
                ) : (
                    <div className="rana-funnel">
                        {steps.map((step, i) => {
                            const prevCount = i > 0 ? steps[i - 1].count : null;
                            const conv = conversionPercent(prevCount, step.count);
                            const ofTotal = funnelTotal > 0 ? Math.round(((step.count || 0) / funnelTotal) * 100) : 0;
                            const widthPercent = funnelTotal > 0 ? Math.max(((step.count || 0) / funnelTotal) * 100, 3) : 0;
                            return (
                                <React.Fragment key={step.key}>
                                    {i > 0 && (
                                        <div className="rana-funnel-conv">
                                            {conv !== null ? `↓ ${conv}% seguiram da etapa anterior` : '↓'}
                                        </div>
                                    )}
                                    <div className="rana-funnel-row">
                                        <div className="rana-funnel-label">
                                            <span className="rana-funnel-label-text">{step.label}</span>
                                            <span className="rana-funnel-label-meta">
                                                <strong>{(step.count || 0).toLocaleString('pt-BR')}</strong>
                                                {i > 0 && <span className="rana-funnel-label-pct"> · {ofTotal}% do total</span>}
                                            </span>
                                        </div>
                                        <div className="rana-funnel-track">
                                            <div className="rana-funnel-fill" style={{ width: `${widthPercent}%` }} />
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                )
            ) : !behavior || behavior.visitors === 0 ? (
                <div className="rana-empty">Ainda sem visitas registradas neste período/segmento.</div>
            ) : (
                <div className="rana-behavior-grid">
                    {buildBehaviorTiles(behavior).map((tile, i) => (
                        <div key={i} className="rana-stat-tile">
                            <div className="rana-stat-value">{tile.value}</div>
                            <div className="rana-stat-label">{tile.label}</div>
                            {tile.sub && <div className="rana-stat-sub">{tile.sub}</div>}
                        </div>
                    ))}
                </div>
            )}
            </div>
            )}
        </div>
    );
};

export default RyanAdminAnalytics;
